import { Button, ButtonVariants } from '@components/button/Button';
import { Error } from '@components/error/Error';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { reportAppError } from '@shared/reportAppError';
import { getWindow } from '@shared/utils/window';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useAuth } from '../../context/AuthContext';
import { SettingsOutletHeader } from '../shared/SettingsOutletHeader';
import {
  disconnectLightspeedIntegration,
  getLightspeedOAuthAuthorizationUrl,
} from './apiHelpers';
import { DisconnectLightspeedModal } from './DisconnectLightspeedModal';
import {
  lightspeedBusinessLocationsQueryKey,
  lightspeedConnectionQueryKey,
  useLightspeedConnection,
} from './hooks';
import { LightspeedConnectionCard } from './LightspeedConnectionCard';
import styles from './LightspeedSettingsPage.scss';

const getIntroText = (isConnected: boolean, needsReauthorization: boolean) => {
  if (!isConnected) {
    return 'Connect your Lightspeed K-Series account so reservations sync to Lightspeed and orders show up in Peak.';
  }
  if (needsReauthorization) {
    return 'Lightspeed no longer accepts the saved sign-in for this restaurant. Reconnect to resume syncing.';
  }
  return 'Your restaurant is linked to Lightspeed K-Series.';
};

const getLoadErrorMessage = (error: unknown): string => {
  if (!error) {
    return '';
  }

  return error instanceof ApiError
    ? error.message
    : 'An internal server error occurred';
};

export const LightspeedSettingsPage = () => {
  const restaurant = useRestaurant();
  const { refreshRestaurants } = useAuth();
  const queryClient = useQueryClient();
  const {
    data: connection,
    error,
    isPending,
  } = useLightspeedConnection(restaurant.id);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const {
    isOpen: isDisconnectModalOpen,
    open: openDisconnectModal,
    close: closeDisconnectModal,
  } = useIsOpen();

  useEffect(() => {
    if (error && !(error instanceof ApiError)) reportAppError(error);
  }, [error]);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const { authorizationUrl } = await getLightspeedOAuthAuthorizationUrl(
        restaurant.id,
      );
      if (!authorizationUrl) {
        errorToast({
          message: 'Lightspeed sign-in is not enabled for this environment.',
        });
        return;
      }

      getWindow().location.assign(authorizationUrl);
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({
          message: 'Could not start Lightspeed sign-in. Please try again.',
        });
        reportAppError(error);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const handleConfirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectLightspeedIntegration(restaurant.id);
      // The list belongs to the account that was connected, not whoever connects next. It is
      // removed, not invalidated, so the card still on screen doesn't ask for it again.
      queryClient.removeQueries({
        queryKey: lightspeedBusinessLocationsQueryKey(restaurant.id),
      });
      closeDisconnectModal();
      successToast({
        message: 'Lightspeed has been disconnected for this restaurant.',
      });
      await queryClient.invalidateQueries({
        queryKey: lightspeedConnectionQueryKey(restaurant.id),
      });
      // The restaurant payload carries the location id that gates Lightspeed features.
      refreshRestaurants();
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({
          message: 'Could not disconnect Lightspeed. Please try again.',
        });
        reportAppError(error);
      }
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleLocationLinked = async () => {
    successToast({ message: 'Your Lightspeed location is linked.' });
    refreshRestaurants();
    await queryClient.invalidateQueries({
      queryKey: lightspeedConnectionQueryKey(restaurant.id),
    });
  };

  const isConnected = connection?.isConnected ?? false;
  const needsReauthorization = connection?.needsReauthorization ?? false;

  const renderConnection = () => {
    if (!connection) {
      return null;
    }

    if (connection.isConnected) {
      return (
        <LightspeedConnectionCard
          restaurantId={restaurant.id}
          businessLocationId={connection.businessLocationId}
          needsReauthorization={needsReauthorization}
          recentPushFailedAt={connection.recentPushFailedAt}
          isReconnecting={isConnecting}
          onReconnect={() => void handleConnect()}
          onDisconnect={openDisconnectModal}
          onLocationLinked={() => void handleLocationLinked()}
        />
      );
    }

    if (!connection.isOAuthConfigured) {
      return (
        <p>
          Lightspeed sign-in is not enabled for this environment yet. Ask Peak
          support if you need help.
        </p>
      );
    }

    return (
      <div className={styles.actions}>
        <Button
          type="button"
          variant={ButtonVariants.Primary}
          onClick={() => void handleConnect()}
          isDisabled={isConnecting}
        >
          Connect Lightspeed
        </Button>
      </div>
    );
  };

  return (
    <div className={styles.root}>
      <SettingsOutletHeader title="Lightspeed" />
      <p>{getIntroText(isConnected, needsReauthorization)}</p>
      <Error className={styles.error} message={getLoadErrorMessage(error)} />
      {isPending ? <p>Loading…</p> : renderConnection()}
      <DisconnectLightspeedModal
        isOpen={isDisconnectModalOpen}
        isDisconnecting={isDisconnecting}
        onClose={closeDisconnectModal}
        onConfirm={() => void handleConfirmDisconnect()}
      />
    </div>
  );
};
